import React from "react";
import { FaFacebook, FaTwitter, FaLinkedin, FaGithub, FaEnvelope, FaHeart, FaArrowUp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useVisitor } from "./extras/VisitorContext";
import { useTheme } from "./Theme/ThemeContext";

export default function Footer() {
  let uniqueVisitors = useVisitor().uniqueVisitors;
  const { currentTheme } = useTheme();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-neutral-300 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl" style={{ background: `rgba(var(--color-primary), 0.15)` }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl" style={{ background: `rgba(var(--color-secondary), 0.15)` }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full blur-3xl" style={{ background: `rgba(var(--color-accent), 0.1)` }} />
      </div>

      {/* Scroll to top button */}
      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
        <button
          onClick={scrollToTop}
          className={`p-3 bg-gradient-to-r ${currentTheme.colors.gradient} rounded-full text-white shadow-lg ${currentTheme.colors.glow} hover:shadow-xl hover:scale-110 transition-all duration-300`}
        >
          <FaArrowUp className="w-4 h-4" />
        </button>
      </div>

      <div className="relative ca-container pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className={`text-2xl font-display font-bold bg-gradient-to-r ${currentTheme.colors.gradientText} bg-clip-text text-transparent`}>CodeArena</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Empowering developers with coding skills, job opportunities, and a
              thriving community 🚀
            </p>
            <div className={`flex items-center gap-2 px-4 py-2.5 ${currentTheme.colors.bg}/60 border ${currentTheme.colors.border} rounded-xl`}>
              <div className="w-2 h-2 bg-theme-primary rounded-full animate-pulse" />
              <span className="text-sm text-gray-300">
                <span className="text-theme-primary font-semibold">{uniqueVisitors}</span> developers visited
              </span>
            </div>
          </div>

          {/* About */}
          <div>
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <div className={`w-1 h-5 bg-gradient-to-b ${currentTheme.colors.gradient} rounded-full`} />
              About
            </h3>
            <ul className="space-y-3">
              {[
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact Us" },
                { to: "/faq", label: "FAQ" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-theme-primary hover:translate-x-1 inline-flex transition-all duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <div className={`w-1 h-5 bg-gradient-to-b ${currentTheme.colors.gradient} rounded-full`} />
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { to: "/courses", label: "Courses" },
                { to: "/jobs", label: "Jobs" },
                { to: "/internships", label: "Internships" },
                { to: "/referrals", label: "Referrals" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-theme-secondary hover:translate-x-1 inline-flex transition-all duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <div className={`w-1 h-5 bg-gradient-to-b ${currentTheme.colors.gradient} rounded-full`} />
              Connect With Us
            </h3>
            <div className="flex gap-3 mb-6">
              {[
                { href: "https://www.facebook.com/padiee.thombre.755", icon: FaFacebook, color: "hover:bg-blue-600", hoverShadow: "hover:shadow-blue-500/40" },
                { href: "https://x.com/PradeepThombre7", icon: FaTwitter, color: "hover:bg-sky-500", hoverShadow: "hover:shadow-sky-500/40" },
                { href: "https://linkedin.com/in/pradeep-thombre", icon: FaLinkedin, color: "hover:bg-blue-700", hoverShadow: "hover:shadow-blue-600/40" },
                { href: "https://github.com/pradeep-thombre", icon: FaGithub, color: "hover:bg-theme-secondary", hoverShadow: "hover:shadow-theme-lg" },
              ].map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-xl ${currentTheme.colors.bg}/60 border ${currentTheme.colors.border} flex items-center justify-center text-gray-400 hover:text-white ${social.color} ${social.hoverShadow} hover:shadow-lg hover:scale-110 transition-all duration-300`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <a
              href="mailto:codearenaui@gmail.com"
              className={`inline-flex items-center gap-2 px-4 py-2.5 ${currentTheme.colors.bg}/60 border ${currentTheme.colors.border} rounded-xl text-sm text-gray-300 hover:bg-theme-secondary/20 hover:text-theme-accent hover:border-theme-accent/30 transition-all duration-200`}
            >
              <FaEnvelope className="w-4 h-4" />
              codearenaui@gmail.com
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`pt-8 border-t ${currentTheme.colors.border} flex flex-col sm:flex-row justify-between items-center gap-4`}>
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} <span className="text-theme-secondary">CodeArena</span>. All rights reserved.
          </p>
          <p className="text-sm text-gray-400 flex items-center gap-1">
            Made with <FaHeart className="w-3 h-3 text-theme-accent animate-pulse" /> in India
          </p>
        </div>
      </div>
    </footer>
  );
}
